import vec2 from '@/helpers/vec2'
import range from '@/helpers/range'
import { hasFlag } from '@/helpers/flags'

import animatedValue from './animatedValue'
import { Color } from './constants'

/**
 * Flags to determine how the crop parts should be rendered.
 * @readonly *
 * @enum {number}
 */
export const Flag = {
  /** The image is rendered as is based on bone orientation and position */
  NONE: 0,
  /** The image is rendered with the x-axis flipped */
  FLIP_X: 0x01,
  /** The image is rendered with the y-axis flipped */
  FLIP_Y: 0x02,
  /** The image is rendered with the both axes flipped */
  FLIP_BOTH: 0x03,
  /** The image is rendered with the x-axis flipped based on bone orientation */
  FLIP_ORIENTATION: 0x04,
  /** The image is rendered with it's width independently of bone width */
  WIDTH_INDEPENDENT: 0x08,
  /** The image is rendered with it's height independently of bone height */
  LENGTH_INDEPENDENT: 0x10,
  /** The image is rendered independently of bone width and height */
  INDEPENDENT: 0x18,
  /** The image is rendered scaled */
  SCALE: 0x20
}

/**
 * Bone volume sides
 * @readonly
 * @enum {number}
 */
const VolumeSide = {
  /** Bone's left side */
  LEFT: 0,
  /** Bone's right side */
  RIGHT: 1
}

/**
 * Bone joint
 * @readonly
 * @enum {number}
 */
const Joint = {
  /** Bone's start joint */
  START: 0,
  /** Bone's end joint */
  END: 1
}

/**
 * Bone widths
 * @readonly
 * @enum {number}
 */
const Width = {
  START: 10,
  END: 5
}

/**
 * Sets bone's length
 * @param {Bone} bone
 * @param {number} length
 * @returns {Bone}
 */
export function setLength (bone, length) {
  bone.length = length
  return updateEnd(bone)
}

/**
 * Sets the bone's current length
 * @param {Bone} bone
 * @param {number} length
 * @returns {Bone}
 */
export function setCurrentLength (bone, length) {
  bone.currentLength = length
  return updateCurrentEnd(bone)
}

/**
 * Returns the length of a bone
 * @param {Bone} bone
 * @returns {number}
 */
export function getLength (bone) {
  return vec2.distanceBetween(bone.start, bone.end)
}

/**
 * Updates bone direction
 * @param {Bone} bone
 * @returns {Bone}
 */
export function updateDirection (bone) {
  vec2.polar(
    bone.direction,
    bone.orientation
  )
  return bone
}

/**
 * Updates bone orientation
 * @param {Bone} bone
 * @returns {Bone}
 */
export function updateOrientation (bone) {
  const [sx, sy] = bone.start
  const [ex, ey] = bone.end
  bone.orientation = Math.atan2(
    ey - sy,
    ex - sx
  )
  return updateDirection(bone)
}

/**
 * Updates bone parent.
 * @param {Bone} bone
 * @param {Array<Bone>} bones
 * @returns {Bone}
 */
export function updateParent (bone, bones) {
  const parent = bones[bone.parent]
  vec2.copy(bone.currentStart, parent.currentEnd)
  vec2.copy(bone.start, parent.end)
  return updateEnd(bone)
}

/**
 * Updates bone length
 * @param {Bone} bone
 * @returns {Bone}
 */
export function updateLength (bone) {
  bone.length = vec2.distanceBetween(bone.start, bone.end)
  return bone
}

/**
 * Updates bone end point
 * @param {Bone} bone
 * @returns {Bone}
 */
export function updateEnd (bone) {
  vec2.add(
    bone.end,
    bone.start,
    vec2.polar(
      bone.end,
      bone.orientation,
      bone.length
    )
  )
  return updateLength(bone)
}

/**
 * Updates bone end point
 * @param {Bone} bone
 * @returns {Bone}
 */
export function updateCurrentEnd (bone) {
  return vec2.add(
    bone.currentEnd,
    bone.currentStart,
    vec2.polar(
      bone.currentEnd,
      bone.orientation,
      bone.currentLength
    )
  )
}

/**
 * Renders the bone inner line
 * @param {CanvasRenderingContext2D} cx
 * @param {Bone} bone
 */
export function renderCurrentLine (cx, bone) {
  const [sx, sy] = bone.currentStart
  const [ex, ey] = bone.currentEnd
  cx.beginPath()
  cx.moveTo(sx, sy)
  cx.lineTo(ex, ey)
  cx.strokeStyle = Color.BONE_CURRENT
  cx.stroke()
}

/**
 * Renders bone joint
 * @param {CanvasRenderingContext2D} cx
 * @param {Bone} bone
 * @param {string} joint
 */
export function renderCurrentJoint (cx, bone, joint) {
  const [x, y] = joint === 0 ? bone.currentStart : bone.currentEnd
  const r = joint === 0 ? Width.START : Width.END
  cx.beginPath()
  cx.arc(x, y, r, 0, Math.PI * 2)
  cx.strokeStyle = Color.BONE_CURRENT
  cx.stroke()

  cx.beginPath()
  cx.moveTo(x - r, y)
  cx.lineTo(x + r, y)
  cx.strokeStyle = Color.BONE_CURRENT
  cx.stroke()

  cx.beginPath()
  cx.moveTo(x, y - r)
  cx.lineTo(x, y + r)
  cx.strokeStyle = Color.BONE_CURRENT
  cx.stroke()
}

/**
 * Renders bone volume
 * @param {CanvasRenderingContext2D} cx
 * @param {Bone} bone
 * @param {string} side
 */
export function renderCurrentVolume (cx, bone, side) {
  const tmp = vec2.create()
  cx.beginPath()
  vec2.copy(tmp, bone.direction)
  if (side === 0) {
    vec2.perpLeft(tmp, tmp)
  } else {
    vec2.perpRight(tmp, tmp)
  }
  vec2.scale(tmp, tmp, Width.START)
  vec2.add(tmp, tmp, bone.currentStart)
  cx.moveTo(tmp[0], tmp[1])
  vec2.copy(tmp, bone.direction)
  if (side === 0) {
    vec2.perpLeft(tmp, tmp)
  } else {
    vec2.perpRight(tmp, tmp)
  }
  vec2.scale(tmp, tmp, Width.END)
  vec2.add(tmp, tmp, bone.currentEnd)
  cx.lineTo(tmp[0], tmp[1])
  cx.strokeStyle = Color.BONE_CURRENT
  cx.stroke()
}

/**
 * Renders the bone inner line
 * @param {CanvasRenderingContext2D} cx
 * @param {Bone} bone
 */
export function renderLine (cx, bone) {
  const [sx, sy] = bone.start
  const [ex, ey] = bone.end
  cx.beginPath()
  cx.moveTo(sx, sy)
  cx.lineTo(ex, ey)
  cx.strokeStyle = Color.BONE
  cx.stroke()
}

/**
 * Renders bone joint
 * @param {CanvasRenderingContext2D} cx
 * @param {Bone} bone
 * @param {string} joint
 */
export function renderJoint (cx, bone, joint) {
  const [x, y] = joint === 0 ? bone.start : bone.end
  const r = joint === 0 ? Width.START : Width.END
  cx.beginPath()
  cx.arc(x, y, r, 0, Math.PI * 2)
  cx.strokeStyle = Color.BONE
  cx.stroke()

  cx.beginPath()
  cx.moveTo(x - r, y)
  cx.lineTo(x + r, y)
  cx.strokeStyle = Color.BONE
  cx.stroke()

  cx.beginPath()
  cx.moveTo(x, y - r)
  cx.lineTo(x, y + r)
  cx.strokeStyle = Color.BONE
  cx.stroke()
}

/**
 * Renders bone volume
 * @param {CanvasRenderingContext2D} cx
 * @param {Bone} bone
 * @param {string} side
 */
export function renderVolume (cx, bone, side) {
  const tmp = vec2.create()
  cx.beginPath()
  vec2.copy(tmp, bone.direction)
  if (side === 0) {
    vec2.perpLeft(tmp, tmp)
  } else {
    vec2.perpRight(tmp, tmp)
  }
  vec2.scale(tmp, tmp, Width.START)
  vec2.add(tmp, tmp, bone.start)
  cx.moveTo(tmp[0], tmp[1])
  vec2.copy(tmp, bone.direction)
  if (side === 0) {
    vec2.perpLeft(tmp, tmp)
  } else {
    vec2.perpRight(tmp, tmp)
  }
  vec2.scale(tmp, tmp, Width.END)
  vec2.add(tmp, tmp, bone.end)
  cx.lineTo(tmp[0], tmp[1])
  cx.strokeStyle = Color.BONE
  cx.stroke()
}

/**
 * Renders bone name
 * @param {CanvasRenderingContext2D} cx
 * @param {Bone} bone
 */
export function renderName (cx, bone) {
  const tmp = vec2.create()
  vec2.copy(tmp, bone.end)
  vec2.substract(tmp, tmp, bone.start)
  vec2.scale(tmp, tmp, 0.5)
  vec2.add(tmp, tmp, bone.start)

  cx.fillStyle = Color.BONE
  cx.fillText(`${bone.name}`, tmp[0], tmp[1])
}

/**
 * Renders the image associated with a bone.
 * @param {CanvasRenderingContext2D} cx
 * @param {SimulationData} data
 * @param {CropData} crop
 * @param {Bone} bone
 */
export function renderImage (cx, data, crop, bone) {
  if (!data) {
    return
  }

  const { BBCH } = data
  const { images } = crop
  const { image: animatedImage, flags } = bone

  // Retrieve the current image based on the BBCH
  const currentImageName = animatedValue.getValueAt(animatedImage, BBCH)
  const image = images.get(currentImageName)
  if (!image) {
    return
  }
  const [sx, sy] = bone.currentStart

  let flipX = 1
  let flipY = 1

  const flipOrientation = hasFlag(flags, Flag.FLIP_ORIENTATION)
  if (!flipOrientation) {
    flipY = hasFlag(flags, Flag.FLIP_Y) ? -1 : 1
    flipX = hasFlag(flags, Flag.FLIP_X) ? -1 : 1
  } else {
    flipX = (bone.orientation < Math.PI * -0.5 || bone.orientation > Math.PI * 0.5) ? -1 : 1
  }

  const length = hasFlag(flags, Flag.LENGTH_INDEPENDENT) ? 1 : bone.currentLength / image.naturalWidth
  const width = hasFlag(flags, Flag.WIDTH_INDEPENDENT) ? 1 : bone.width / image.naturalHeight

  const [scaleX, scaleY] = [
    hasFlag(flags, Flag.SCALE) ? flipY * length : flipY * length,
    hasFlag(flags, Flag.SCALE) ? flipX * length : flipX * width
  ]

  cx.save()
  cx.translate(sx, sy)
  cx.rotate(bone.orientation)
  cx.scale(scaleX, scaleY)
  cx.drawImage(image, 0, -image.height >> 1)
  cx.restore()
}

/**
 * Renders bone image debug information
 * @param {CanvasRenderingContext2D} cx
 * @param {SimulationData} data
 * @param {CropData} crop
 * @param {Bone} bone
 */
export function renderImageDebug (cx, data, crop, bone) {
  if (!data) {
    return
  }
  const { BBCH } = data
  const { images } = crop
  const { image: animatedImage, flags } = bone

  // Retrieve the current image based on the BBCH
  const currentImageName = animatedValue.getValueAt(animatedImage, BBCH)
  const image = images.get(currentImageName)
  if (!image) {
    return
  }
  const [sx, sy] = bone.start

  let flipX = 1
  let flipY = 1

  const flipOrientation = hasFlag(flags, Flag.FLIP_ORIENTATION)
  if (!flipOrientation) {
    flipY = hasFlag(flags, Flag.FLIP_Y) ? -1 : 1
    flipX = hasFlag(flags, Flag.FLIP_X) ? -1 : 1
  } else {
    flipX = (bone.orientation < Math.PI * -0.5 || bone.orientation > Math.PI * 0.5) ? -1 : 1
  }

  const length = hasFlag(flags, Flag.LENGTH_INDEPENDENT) ? 1 : bone.currentLength / image.naturalWidth
  const width = hasFlag(flags, Flag.WIDTH_INDEPENDENT) ? 1 : bone.width / image.naturalHeight

  const [scaleX, scaleY] = [
    hasFlag(flags, Flag.SCALE) ? flipY * length : flipY * length,
    hasFlag(flags, Flag.SCALE) ? flipX * length : flipX * width
  ]

  cx.save()
  cx.translate(sx, sy)
  cx.rotate(bone.orientation)

  if (process.env.NODE_ENV === 'development' && crop.debug.renderImageName) {
    cx.font = '16px monospace'
    cx.textAlign = 'left'
    cx.textBaseline = 'middle'
    cx.fillStyle = '#f0f'
    cx.fillText(`Image: ${currentImageName}`, 0, 0)
  }

  cx.scale(scaleX, scaleY)
  if (process.env.NODE_ENV === 'development' && crop.debug.renderImageRect) {
    cx.strokeStyle = '#f0f'
    cx.strokeRect(0, -image.height >> 1, image.width, image.height)
  }
  cx.restore()
}

/**
 * Render a bone
 * @param {CanvasRenderingContext2D} cx
 * @param {SimulationData} data
 * @param {CropData} crop
 * @param {Bone} bone
 */
export function render (cx, data, crop, bone) {
  renderImage(cx, data, crop, bone)
}

/**
 * Updates a bone
 * @param {SimulationData} data
 * @param {CropData} crop
 * @param {Bone} bone
 */
export function update (data, crop, bone) {
  if (!data) {
    return
  }
  const { BBCH } = data
  const { bones } = crop
  const { BBCH: [minBBCH, maxBBCH] } = bone
  vec2.copy(bone.currentStart, bone.start)
  vec2.copy(bone.currentEnd, bone.end)
  updateLength(bone)
  updateOrientation(bone)
  if (bone.parent >= 0) {
    updateParent(bone, bones)
  }
  setCurrentLength(bone, range.to(range.clamp(range.from(BBCH, minBBCH, maxBBCH), 0, 1), 0, bone.length))
}

/**
 * Updates all bones
 * @param {SimulationData} data
 * @param {CropData} cropData
 */
export function updateAll (data, crop) {
  const { bones } = crop
  for (const bone of bones) {
    update(data, crop, bone)
  }
}

/**
 * Renders bone debug volume
 * @param {CanvasRenderingContext2D} cx
 * @param {SimulationData} data
 * @param {CropData} crop
 * @param {Bone} bone
 */
export function renderBoneDebug (cx, data, crop, bone) {
  if (process.env.NODE_ENV !== 'development') {
    return
  }

  if (crop.debug.renderCurrentBone) {
    renderCurrentLine(cx, bone)
  }
  if (crop.debug.renderBone) {
    renderLine(cx, bone)
  }

  if (crop.debug.renderCurrentBone) {
    renderCurrentJoint(cx, bone, Joint.START)
  }
  if (crop.debug.renderBone) {
    renderJoint(cx, bone, Joint.START)
  }

  if (crop.debug.renderCurrentBone) {
    renderCurrentJoint(cx, bone, Joint.END)
  }
  if (crop.debug.renderBone) {
    renderJoint(cx, bone, Joint.END)
  }

  if (crop.debug.renderCurrentBone) {
    renderCurrentVolume(cx, bone, VolumeSide.LEFT)
  }
  if (crop.debug.renderBone) {
    renderVolume(cx, bone, VolumeSide.LEFT)
  }

  if (crop.debug.renderCurrentBone) {
    renderCurrentVolume(cx, bone, VolumeSide.RIGHT)
  }
  if (crop.debug.renderBone) {
    renderVolume(cx, bone, VolumeSide.RIGHT)
  }

  if (crop.debug.renderBone) {
    renderName(cx, bone)
  }
}

/**
 * Renders debug information
 * @param {CanvasRenderingContext2D}
 * @param {SimulationData}
 * @param {CropData}
 * @param {Bone}
 */
export function renderDebug (cx, data, crop, bone) {
  renderBoneDebug(cx, data, crop, bone)
  renderImageDebug(cx, data, crop, bone)
}

/**
 * Renders all bones
 * @param {CanvasRenderingContext2D} cx
 * @param {SimulationData} data
 * @param {CropData} crop
 */
export function renderAll (cx, data, crop) {
  const { bones } = crop
  for (const bone of bones) {
    render(cx, data, crop, bone)
  }
  if (process.env.NODE_ENV !== 'development') {
    return
  }
  for (const bone of bones) {
    renderDebug(cx, data, crop, bone)
  }
}

export default {
  setLength,
  setCurrentLength,
  updateDirection,
  updateOrientation,
  updateCurrentEnd,
  updateEnd,
  update,
  updateAll,
  renderVolume,
  renderLine,
  renderJoint,
  render,
  renderAll
}
